import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AlertService, AlertType } from '../../services/alert.service';
import { PedidoExtracaoService } from '../../services/apis/batch-extracao-dados/pedido-extracao.service';
import { PedidoExtracaoRequest } from '../../services/requests/batch-extracao-dados/pedido-extracao.request';
import { PostPedidoExtracaoResponse } from '../../services/responses/batch-extracao-dados/post-pedido-extracao.response';
import { StatusExtracaoEnum } from '../../enums/batch-extracao-dados/status-extracao.enum';
import { GetPedidoExtracaoResponse } from '../../services/responses/batch-extracao-dados/get-pedido-extracao.response';
import { Router } from '@angular/router';

export interface FiltroExtracao {
  label: string;
  formControlName: string;
  valor: unknown;
  valorExibicao?: string | string[];
}

export interface ExtracaoDadosData {
  titulo: string;
  tipoExtracao: number;
  origemPedidoExportacao: number; // int (valores de OrigemPedidoExportacaoEnum)
  possuiAutomacao: boolean;
  filtros: FiltroExtracao[];
}

@Component({
  selector: 'app-extracao-dados-modal',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatProgressSpinnerModule, MatIconModule],
  templateUrl: './extracao-dados-modal.component.html',
  styleUrls: ['./extracao-dados-modal.component.css'],
})
export class ExtracaoDadosModalComponent implements OnInit, OnDestroy {
  public view: 'confirmacao' | 'acompanhamento' = 'confirmacao';
  public _isLoadingConfirmacao = false;

  public statusFinal: 'sucesso' | 'erro' | 'semDados' | null = null;
  public linkDownload: string | null = null;
  public mensagemErro: string = 'Ocorreu um erro ao processar sua solicitação. Por favor, contate o suporte.';
  public mensagemSucesso: string = '';
  public mensagemSemDados: string = 'Nenhum dado encontrado para os filtros aplicados.';

  private pollingSubscription: Subscription;
  private idPedidoExtracao: number;

  constructor(
    public dialogRef: MatDialogRef<ExtracaoDadosModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExtracaoDadosData,
    private pedidoExtracaoService: PedidoExtracaoService,
    private alertService: AlertService,
    private router: Router
  ) { }

  ngOnInit(): void { }
  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  confirmarExtracao(): void {
    if (this._isLoadingConfirmacao) return;
    this._isLoadingConfirmacao = true;

    const filtrosParaApi = this.data.filtros.reduce(
      (obj, item) => {
        obj[item.formControlName] = item.valor;
        return obj;
      },
      {} as Record<string, unknown>
    );

    const request = new PedidoExtracaoRequest();
    request.idTipoExtracao = this.data.tipoExtracao;
    request.jsonFiltros = JSON.stringify(filtrosParaApi);
    request.origemPedidoExportacao = this.data.origemPedidoExportacao;

    this.pedidoExtracaoService.post(request).subscribe({
      next: (response: PostPedidoExtracaoResponse) => {
        this._isLoadingConfirmacao = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        this.idPedidoExtracao = response.idPedidoExtracao;
        this.view = 'acompanhamento';
        this.iniciarPollingStatus();
      },
      error: err => {
        this._isLoadingConfirmacao = false;
        this.alertService.show('Erro', `Falha ao criar pedido: ${err.message}`, AlertType.error);
      },
    });
  }

  iniciarPollingStatus(): void {
    this.pollingSubscription = timer(0, 10000)
      .pipe(switchMap(() => this.pedidoExtracaoService.getById(this.idPedidoExtracao)))
      .subscribe({
        next: (response: GetPedidoExtracaoResponse) => {
          if (response.isError) {
            this.statusFinal = 'erro';
            this.mensagemErro = response.errorDescription || this.mensagemErro;
            this.pollingSubscription.unsubscribe();
            return;
          }

          switch (response.pedidoExtracao.idStatusExtracao) {
            case StatusExtracaoEnum.ProcessadoComSucesso:
              if (response.pedidoExtracao.urlArquivo) {
                this.statusFinal = 'sucesso';
                this.linkDownload = response.pedidoExtracao.urlArquivo;
                this.mensagemSucesso = response.pedidoExtracao.erroExtracao ?? '';
              } else {
                this.statusFinal = 'semDados';
                this.mensagemSemDados = response.pedidoExtracao.erroExtracao || this.mensagemSemDados;
              }
              this.pollingSubscription.unsubscribe();
              break;
            case StatusExtracaoEnum.ProcessadoComErro:
              this.statusFinal = 'erro';
              this.mensagemErro = response.pedidoExtracao.erroExtracao || response.errorDescription || 'O processamento encontrou um erro.';
              this.pollingSubscription.unsubscribe();
              break;
            default:
              break;
          }
        },
        error: err => {
          this.statusFinal = 'erro';
          this.mensagemErro = `Falha ao verificar status: ${err.message}`;
          this.alertService.show('Erro', this.mensagemErro, AlertType.error);
          this.pollingSubscription.unsubscribe();
        },
      });
  }

  fechar(): void {
    this.dialogRef.close();
  }

  visualizarAutomacoes(): void {
    this.router.navigate([
      '/home/ferramentas/automacao-extracao-dados',
      { idTipoExtracao: this.data.tipoExtracao },
    ]);
    this.dialogRef.close();
  }

  public valoresExibicao(filtro: FiltroExtracao): string[] {
    if (filtro.valorExibicao !== undefined) {
      return Array.isArray(filtro.valorExibicao) ? filtro.valorExibicao : [filtro.valorExibicao];
    }

    if (Array.isArray(filtro.valor)) {
      return filtro.valor.map(valor => String(valor));
    }

    return [String(filtro.valor)];
  }
}
